import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useState } from "react";

import type { components } from "@contracts/generated/editor-service";
import { useValidationStore } from "@/state/validationState";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";
import { isTerminalQueryError } from "./queryClient";
import { useRetryPausedQueryByKey } from "./useRetryPausedQuery";

/**
 * The versioned shared clinical library (#28's API, #32's surface).
 *
 * Aliases into the generated contract, like `hooks.ts`: change the contract,
 * regenerate, and these names either still resolve or fail to compile.
 */
export type LibraryAsset = components["schemas"]["LibraryAsset"];
export type LibraryAssetList = components["schemas"]["LibraryAssetList"];
export type AssetCapabilities = components["schemas"]["AssetCapabilities"];
/** #214's stable archive-refusal vocabulary — the key type label maps satisfy. */
export type ArchiveRefusalReason = components["schemas"]["ArchiveRefusalReason"];
export type AssetVersionSummary =
  components["schemas"]["AssetVersionSummary"];
export type AssetVersionList = components["schemas"]["AssetVersionList"];
export type LibraryAssetVersionDetail =
  components["schemas"]["LibraryAssetVersionDetail"];
export type AssetAdoptionList = components["schemas"]["AssetAdoptionList"];
export type AssetAdoptionRef = components["schemas"]["AssetAdoptionRef"];
export type CreateAssetVersionRequest =
  components["schemas"]["CreateAssetVersionRequest"];
export type CreateLibraryAssetRequest =
  components["schemas"]["CreateLibraryAssetRequest"];
export type LibraryAssetType = components["schemas"]["LibraryAssetType"];
export type RulesSubstitutionProjection =
  components["schemas"]["RulesSubstitutionProjection"];

/**
 * The licensed-inventory verdict this bundle received for one asset, if any.
 *
 * The v5 contract requires the field, but browser and service deployments are
 * not atomic: a newly deployed bundle can briefly read an otherwise valid v4
 * asset from a service that does not publish it yet. Keep that version-skew
 * tolerance at the response boundary so every consumer degrades from the same
 * fact instead of dereferencing a field that is only statically guaranteed.
 */
export function licensedInventoryVerdict(
  asset: LibraryAsset,
): AssetCapabilities["licensedInventory"] | undefined {
  return (
    asset.capabilities as {
      licensedInventory?: AssetCapabilities["licensedInventory"];
    }
  ).licensedInventory;
}

/**
 * The alert-matrix family (#35). Aliased here for the same reason as every
 * other name in this file: the contract is the definition, and a shape that
 * drifts from it stops compiling rather than stops working.
 *
 * `AlertChannel` is `email | sms | webhook`. Nothing in this union carries a
 * destination VALUE — `destinationRefs` holds opaque, slug-shaped references
 * that the deployment-mounted #637 authority governs.
 */
export type AlertChannel = components["schemas"]["AlertChannel"];
export type AlertSeverity = components["schemas"]["AlertSeverity"];
export type AlertFlag = components["schemas"]["AlertFlag"];
export type AlertDomain = components["schemas"]["AlertDomain"];
export type DomainRule = components["schemas"]["DomainRule"];
export type ComboRule = components["schemas"]["ComboRule"];
export type SeverityRouting = components["schemas"]["SeverityRouting"];

/**
 * The questionnaire family (#368), aliased for the same reason as every other
 * name in this file: the contract is the definition.
 *
 * `ScoringMethod` is `sum | mean` and is OPTIONAL on the content — an absent
 * field means `sum`, which is what keeps every questionnaire version stored
 * before #368 readable. `QuestionnaireItem.reverseScored` is optional for the
 * same reason; the reversed contribution is derived from the response scale
 * rather than stored.
 */
export type QuestionnaireItem = components["schemas"]["QuestionnaireItem"];
export type ResponseOption = components["schemas"]["ResponseOption"];
export type SeverityBand = components["schemas"]["SeverityBand"];
export type ValidityCheck = components["schemas"]["ValidityCheck"];
export type ScoringMethod = components["schemas"]["ScoringMethod"];

/** The project's whole pin set, and the identity its replacement pins against. */
export type ProjectAdoptions = components["schemas"]["ProjectAdoptions"];
export type AdoptionPin = components["schemas"]["AdoptionPin"];
/**
 * What a client WRITES for one pin: `{assetId, version}` and nothing else.
 *
 * The response pin (`AdoptionPin`) also carries `assetType`, joined
 * server-side; the request shape deliberately does not, and the service
 * refuses an echoed one (`extra="forbid"`) rather than ignore it. Build request
 * items through `lib/adoptions.ts`'s `pinRequest`, never by spreading a pin.
 */
export type AdoptionPinRequest = components["schemas"]["AdoptionPinRequest"];
export type ReplaceProjectAdoptionsRequest =
  components["schemas"]["ReplaceProjectAdoptionsRequest"];

/**
 * The typed evidence a refused `PUT /adoptions` carries in `error.details`
 * (#632), and the registry vocabulary its items speak.
 *
 * The contract's own note on `PlacementRefusalCode` binds the consumers here:
 * frontend label maps must TOLERATE unknown members, because the registry's
 * vocabulary widens with policy versions rather than with this contract.
 */
export type AdoptionPreflightRefusal =
  components["schemas"]["AdoptionPreflightRefusal"];
export type AdoptionPreflightItem =
  components["schemas"]["AdoptionPreflightItem"];
export type AdoptionPreflight = components["schemas"]["AdoptionPreflight"];

/**
 * Which server-owned write an adoption decision goes through (#749, #634, #867).
 *
 * `adoptions` is the whole-set `PUT /adoptions`; the three activations adopt one
 * family's pins AND derive the runtime registration those pins need. Each lane
 * has a `…/preflight` twin that decides the same set without writing, and
 * `useAdoptionPreflightQuery` below addresses the twin from the same value the
 * write is chosen by — so a dry run can never be asked of a different lane
 * than the write it stands in for.
 */
export type AdoptionWriteLane =
  | "adoptions"
  | "pathway-activation"
  | "main-concern-activation"
  | "closing-activation"
  | "background-activation";
export type PlacementVerdictState =
  components["schemas"]["PlacementVerdictState"];
export type PlacementRefusalCode =
  components["schemas"]["PlacementRefusalCode"];
export type AlertDestinationRegistry =
  components["schemas"]["AlertDestinationRegistry"];
export type AlertDestinationRegistryEntry =
  components["schemas"]["AlertDestinationRegistryEntry"];

/**
 * Narrow one of the library-specific flat fields carried by `ApiError`.
 *
 * The shared transport deliberately retains the complete error envelope rather
 * than acquiring a property for every domain. Library controls still need the
 * service's field pointer and stale-version pin, so this module owns their
 * runtime narrowing just as the archive and publishing domains own theirs.
 */
function libraryEnvelopeString(
  error: unknown,
  key: "field" | "currentAssetVersion" | "currentAdoptionsVersion",
): string | undefined {
  if (!(error instanceof ApiError)) {
    return undefined;
  }
  const value = error.envelope[key];
  return typeof value === "string" ? value : undefined;
}

export function libraryErrorField(error: unknown): string | undefined {
  return libraryEnvelopeString(error, "field");
}

export function currentAssetVersionFromError(
  error: unknown,
): string | undefined {
  return libraryEnvelopeString(error, "currentAssetVersion");
}

/**
 * The pin set's current identity, carried on `stale_adoptions_version` so a
 * refused replacement recovers in one round trip rather than by guessing.
 */
export function currentAdoptionsVersionFromError(
  error: unknown,
): string | undefined {
  return libraryEnvelopeString(error, "currentAdoptionsVersion");
}

/**
 * The `AdoptionPreflightRefusal` an `adoption_preflight_refused` 409 carries in
 * `error.details`, or null (#632, consumed for #612's removal dialog).
 *
 * Validated structurally rather than cast, the same discipline
 * `archiveRefusalFromError` applies and for the same reason: `details` is a
 * server-controlled payload, and a cast would turn a malformed body into a
 * runtime crash inside the recovery path that exists to explain a refusal.
 * `refusals` is contractually non-empty ("The REFUSED items only", min 1), so
 * an empty array is treated as absent rather than rendered as a refusal that
 * refuses nothing.
 */
export function adoptionPreflightRefusalFromError(
  error: unknown,
): AdoptionPreflightRefusal | null {
  if (!(error instanceof ApiError) || error.code !== "adoption_preflight_refused") {
    return null;
  }
  const details = error.details;
  if (typeof details !== "object" || details === null) {
    return null;
  }
  const refusals = (details as { refusals?: unknown }).refusals;
  if (!Array.isArray(refusals) || refusals.length === 0) {
    return null;
  }
  return details as AdoptionPreflightRefusal;
}

/**
 * The content union served only at an explicit version — there is deliberately
 * no "content at latest" read anywhere in the contract, so an export can
 * resolve exactly what a clinician saw without consulting a mutable pointer.
 */
export type LibraryAssetContent = LibraryAssetVersionDetail["content"];

/** The typed alert-matrix arm of that union (#35). */
export type AlertMatrixContent = Extract<
  LibraryAssetContent,
  { kind: "alert_matrix" }
>;

/** The typed questionnaire arm of that union (#368). */
export type QuestionnaireContent = Extract<
  LibraryAssetContent,
  { kind: "questionnaire" }
>;

/** The service-owned parts manifest (#491), aliased like everything else here. */
export type LibraryPartManifest = components["schemas"]["LibraryPartManifest"];

/**
 * Which roles the SERVICE names for each parts-shaped type (#491).
 *
 * The one read that lets a named-document surface exist without a second copy
 * of the manifest in the browser: `library.py`'s own preamble forbids restating
 * it here, and #300 deleted the last mirror. What a type requires and allows is
 * asked, never assumed.
 *
 * IT IS DELIBERATELY A SEPARATE QUERY FROM THE HEAD READ, and that separation is
 * asserted by test. The editor's concurrency pin is taken from the version
 * detail at load (`ui/library/AssetEditor.tsx:38-53`); this read describes the
 * SERVICE rather than the asset, so it must never re-seed a draft, never move a
 * pin, and never block an editor that already has content in hand. A manifest
 * that resolves late — a cold cache, a slow deployment read — arrives as extra
 * knowledge about which documents may be ADDED, and changes nothing about the
 * document the clinician is editing.
 *
 * Cacheable and shared: the key names no asset, so every surface that needs the
 * roster reads one cache entry.
 */
export function useLibraryPartManifestQuery(): UseQueryResult<LibraryPartManifest> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.libraryPartManifest,
    async queryFn() {
      return unwrap(await client.GET("/v1/library/part-manifest", {}));
    },
  });
}

/** The server-owned placement/policy registry (#611), aliased like the rest. */
export type PlacementRegistry = components["schemas"]["PlacementRegistry"];

/**
 * The Pathway family/variant contract (#642), aliased like every other name in
 * this file: the generated contract is the definition, and a shape that drifts
 * from it stops compiling rather than stops working.
 *
 * `PathwayStep` is a DISCRIMINATED union on `editability`, and the discriminator
 * is what keeps this surface honest. `editable` is a `prompt_text` step carrying
 * `text`; `protected` is a `structured` one carrying no data field at all and
 * saying `reason: "semantic_schema_unavailable"`; `typed` (#911) is a
 * `structured` one whose `documentSchema` names a schema the service publishes
 * and whose `document` carries it in the engine's own key spelling.
 *
 * The generic value is still absent from `protected`, deliberately — "the
 * dedicated Pathway authoring contract cannot be mistaken for a raw-JSON editor"
 * (`services/editor-service/src/editor_service/schemas/library.py`) — so a
 * browser cannot render an engine-owned document into a writable field even by
 * mistake. A `typed` step is the exception the service ITSELF states, role by
 * role; its form is #912's, and this leaf only stops calling those two steps
 * protected.
 */
export type PathwayAsset = components["schemas"]["PathwayAsset"];
export type PathwayVariantAsset =
  components["schemas"]["PathwayVariantAsset"];
export type PathwayFamilyList = components["schemas"]["PathwayFamilyList"];
export type PathwayFamily = components["schemas"]["PathwayFamily"];
export type PathwayFamilyDefault =
  components["schemas"]["PathwayFamilyDefault"];
export type PathwayFamilyVariant =
  components["schemas"]["PathwayFamilyVariant"];
export type PathwayPromptStep = components["schemas"]["PathwayPromptStep"];
export type PathwayProtectedStep =
  components["schemas"]["PathwayProtectedStep"];
export type PathwayScreeningStep =
  components["schemas"]["PathwayScreeningStep"];
export type PathwayStep =
  | PathwayPromptStep
  | PathwayProtectedStep
  | PathwayScreeningStep;

/**
 * The project-side selection state, and the policy that derives it.
 *
 * BOTH ARE RESPONSE-ONLY, and that is the load-bearing fact for every control
 * built on them. `PathwaySelection.authority` has one legal value, `"server"`,
 * and `PathwaySelectionPolicy` publishes no request-shaped mode or order field:
 * "A project submits exact immutable pins; preflight reads the effective
 * engine-owned registration, derives this state, and persists it with those
 * pins." A browser therefore RENDERS Always/Dynamic and never elects it — a
 * control that offered the choice would be asserting an authority the contract
 * says the client does not hold.
 */
export type PathwaySelection = components["schemas"]["PathwaySelection"];
export type PathwaySelectionMode =
  components["schemas"]["PathwaySelectionMode"];
export type PathwaySelectionPolicy =
  components["schemas"]["PathwaySelectionPolicy"];

/**
 * WHICH LANGUAGES a family may not omit, from the one authority that decides.
 *
 * The registry states the rule (`FamilyPolicy.languages.required`) and
 * `library.validate_required_languages` refuses the write against the same
 * projection, so a browser that reads this and a server that refuses can never
 * disagree — which is the whole reason this is a fetch rather than a constant.
 * A hardcoded `["he", "en"]` here would be the second manifest `library.py`'s
 * preamble forbids, free to drift the day the pack gains a third language.
 *
 * Empty for an absent or still-loading document, which is the honest degraded
 * state: the surface offers no language control it cannot justify, and the
 * server's refusal — which does not depend on this read — stays the authority.
 *
 * Same separation and same cache discipline as the part manifest above: it
 * describes the SERVICE, never the asset, so it must not block or re-seed an
 * editor that already has content in hand.
 */
export function useLibraryPlacementRegistryQuery(): UseQueryResult<PlacementRegistry> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.libraryPlacementRegistry,
    async queryFn() {
      return unwrap(await client.GET("/v1/library/placement-registry", {}));
    },
  });
}

/**
 * The deployment-mounted, value-free Alert destination authority (#637).
 *
 * Separate from asset content and never used to seed a stored draft. A failed
 * read leaves existing immutable refs visible/removable; the server write gate
 * remains fail-closed and reports the same dependency as unavailable.
 */
export function useAlertDestinationRegistryQuery(): UseQueryResult<AlertDestinationRegistry> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.alertDestinationRegistry,
    async queryFn() {
      return unwrap(
        await client.GET("/v1/library/alert-destination-registry", {}),
      );
    },
  });
}

/**
 * The required languages this document declares for one family, or `[]`.
 *
 * A tiny projection rather than a lookup inline at every call site: the shape
 * `families[].languages.required` is the contract's, and reading it in one place
 * keeps a surface from having to know the document's layout to ask a one-word
 * question. `[]` covers both "no document yet" and "this family requires none",
 * which the callers treat identically — neither offers a language control.
 */
export function requiredLanguagesFor(
  registry: PlacementRegistry | undefined,
  assetType: LibraryAssetType | string,
): string[] {
  const family = registry?.families.find(
    (entry) => entry.assetType === assetType,
  );
  return family?.languages.required ?? [];
}

/**
 * The server's own Pathway selection policy, or `undefined`.
 *
 * THE MAXIMUM-DYNAMIC RULE IS READ, NEVER RESTATED. The ratified contract says
 * "a project may have at most three active `Dynamic` variants", and the number
 * three appears in this repository in exactly one place a client may consult:
 * `placement_registry.MAX_DYNAMIC_PATHWAYS`, published as
 * `pathwaySelection.maxDynamic` and enforced by `adoption_preflight`. A literal
 * `3` in the browser would be a second rule — free to disagree with the one that
 * actually refuses the write the day the registry version moves — and the audit
 * already recorded what that costs: PW-PRJ-10 is a Lovable control that
 * "declares max 2 but persists 3".
 *
 * `undefined` for an absent or still-loading document, on the same terms as
 * `requiredLanguagesFor` above: a surface that cannot source the cap says so
 * rather than showing one. The server's refusal does not depend on this read.
 */
export function pathwaySelectionPolicyOf(
  registry: PlacementRegistry | undefined,
): PathwaySelectionPolicy | undefined {
  return registry?.pathwaySelection;
}

/**
 * Whether this family's assets may be AUTHORED in the Editor at all (#638).
 *
 * The same discipline `requiredLanguagesFor` states one projection up, for the
 * other rule that would otherwise be a constant here: the registry publishes
 * `families[].authoring.state` and `library.refuse_governed_authoring` refuses
 * the write against the same set, so a browser that reads this and a server that
 * refuses cannot disagree. A hardcoded `["questionnaire"]` in a surface would be
 * free to drift the day a second family is governed — and would then offer a
 * create control whose submit is refused, which is the dead affordance the
 * shell's invariants forbid.
 *
 * `true` for an absent or still-loading document, which is the honest degraded
 * state HERE and the opposite of the language projection's: withholding a
 * control on no evidence would remove a capability from eight families because
 * one read had not answered, while offering one the service refuses costs a
 * clinician a 403 that names its reason. The server's refusal does not depend on
 * this read either way.
 */
export function familyAuthoringIsOpen(
  registry: PlacementRegistry | undefined,
  assetType: LibraryAssetType | string,
): boolean {
  const family = registry?.families.find(
    (entry) => entry.assetType === assetType,
  );
  // `authoring` first appears in registry v5. Optional chaining is deliberate
  // even though the generated v5 type requires it: during a rolling deploy a
  // v4 service still returns the family, just without this nested member.
  // Absence keeps the documented fail-open degraded state and the server
  // remains the write gate. A PRESENT but unknown future state is different:
  // this bundle cannot claim that it means open, so only today's explicit
  // `open` state enables authoring.
  const state = family?.authoring?.state;
  return state === undefined || state === "open";
}

/**
 * The roles whose authored document merges onto a base document THAT EXISTS.
 *
 * Two published facts have to hold together before a surface may tell a
 * clinician that the keys they leave out stay inherited, and this projection
 * requires both.
 *
 * 1. **The strategy.** `placement.parts[].mergeStrategy` is
 *    `structured_deep_merge`, which Placement Registry v2 publishes for every
 *    emitted part and which the engine's own composition follows.
 * 2. **Something to inherit FROM.** `placement.kind` is `fixed`. A fixed
 *    family's parts land at the family's one server-owned location — for
 *    `language_rules` that is `_FIXED_PARTS`, base canon's own five relpaths,
 *    pinned to `seed/manifest.yaml` by test — so a base document is there by
 *    construction.
 *
 * THE SECOND CONDITION IS WHY THIS IS NOT A ONE-LINE FILTER. A `coordinate`
 * family's path template carries `{slug}` (and `{parent}`), a final segment the
 * CLINICIAN invents, and base canon ordinarily carries no file there — the
 * service says so twice, in `support_loader_proof.py`'s "deliberately a slug
 * base canon does NOT carry" and in the engine lane's `_base_value`, which
 * returns `None` for exactly that case and then composes the authored document
 * unchanged. `main_concern`, `pathway` and `pathway_variant` all publish
 * `structured_deep_merge` parts, so a filter on the strategy alone claims
 * inheritance from a base document that need not exist — a sentence that is
 * false on three families this ticket does not touch.
 *
 * It is deliberately the CONSERVATIVE reading. Base canon does sometimes carry
 * a coordinate an authored asset lands on, and the sentence would be true
 * there; the registry publishes nothing that says which, and a surface that
 * guessed would be inventing composition semantics the engine owns. Saying
 * nothing where the published policy does not decide is the same discipline
 * `[]` already encodes for an absent registry: silence under-informs, a wrong
 * claim misinforms, and only one of those is a defect in a statement about
 * runtime content.
 *
 * This matters on screen because the alternative is a lie of omission. A
 * clinician editing `substitutions` sees a short JSON document and would
 * reasonably read it as everything the runtime has; it is not, and the ratified
 * contract requires the surface to say so.
 *
 * `[]` for an absent or still-loading document, on the same terms as
 * `requiredLanguagesFor`: the surface makes no claim it cannot source.
 */
export function inheritingRolesFor(
  registry: PlacementRegistry | undefined,
  assetType: LibraryAssetType | string,
): string[] {
  const family = registry?.families.find(
    (entry) => entry.assetType === assetType,
  );
  if (family?.placement.kind !== "fixed") {
    return [];
  }
  return (family.placement.parts ?? [])
    .filter((part) => part.mergeStrategy === "structured_deep_merge")
    .map((part) => part.role);
}

/**
 * The whole catalog in one read.
 *
 * Catalog reads carry no content by contract, so the full list is cheap, and
 * one cache entry per `includeArchived` keeps every rail section consistent
 * with every other — a per-section fetch could show one section pre-archive
 * and its neighbour post-archive.
 */
export function useLibraryAssetsQuery(
  includeArchived: boolean,
): UseQueryResult<LibraryAssetList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.libraryAssets(includeArchived),
    // The abort signal is passed through so a fetch nobody observes any more
    // is cancelled rather than allowed to land: with the lifecycle mutation
    // reconciling this entry from the write's response, a request that
    // started before the write and lands after it would re-mark the
    // pre-archive rows as fresh for an inactive (not refetched) entry.
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/library/assets", {
          params: { query: includeArchived ? { includeArchived: true } : {} },
          signal,
        }),
      );
    },
  });
}

/**
 * The Pathway families the service groups, with each member's exact head steps.
 *
 * A SECOND READ RATHER THAN A CLIENT-SIDE GROUPING OF THE FLAT CATALOG, and the
 * ticket that owns the model says why: "One explicit family/default and its
 * variants; never grouped by name." Parent identity is `parentPathwayId`, the
 * stored foreign key, and the catalog list carries it on `PathwayVariantAsset`
 * — so a browser COULD build the tree. What it could not build is the rest of
 * this response: the steps of each member's exact head, which the catalog omits
 * by contract, and the archived-default rule below, which is a service policy
 * rather than an arithmetic.
 *
 * THE ARCHIVED RULE IS THE SERVICE'S. With `includeArchived` false, an archived
 * default that still owns an ACTIVE variant is returned anyway, because
 * "omitting it would turn that variant into an apparently parentless row and
 * contradict the family contract" (`library.list_pathway_families`). A surface
 * therefore never has to decide whether a parent it cannot see exists; it reads
 * `archivedAt` on the default and says so.
 *
 * One cache entry per `includeArchived`, for `useLibraryAssetsQuery`' reason.
 */
export function usePathwayFamiliesQuery(
  includeArchived: boolean,
): UseQueryResult<PathwayFamilyList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.libraryPathwayFamilies(includeArchived),
    // `signal` passed through for the same reason the catalog list passes it:
    // a lifecycle write reconciles this family's rows from its own response,
    // and a read that started before the write and lands after it, with nobody
    // observing, would re-mark the pre-archive answer as fresh.
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/library/pathway-families", {
          params: { query: includeArchived ? { includeArchived: true } : {} },
          signal,
        }),
      );
    },
  });
}

export function useLibraryAssetQuery(
  assetId: string,
): UseQueryResult<LibraryAsset> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.libraryAsset(assetId),
    // `signal` passed through for the same reason as the list above: this
    // entry is reconciled from a lifecycle write's response, and a head read
    // that started before the write and lands after it, with nobody
    // observing, would otherwise re-mark the pre-archive head as fresh.
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/library/assets/{assetId}", {
          params: { path: { assetId } },
          signal,
        }),
      );
    },
    enabled: assetId.length > 0,
  });
}

export function useAssetVersionsQuery(
  assetId: string,
): UseQueryResult<AssetVersionList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.assetVersions(assetId),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/library/assets/{assetId}/versions", {
          params: { path: { assetId } },
          signal,
        }),
      );
    },
    enabled: assetId.length > 0,
  });
}

/**
 * The version history of SEVERAL assets at once, for a surface whose subject is
 * a set of adoptions rather than one asset (#443).
 *
 * WHY IT IS A READ AT ALL. Neither `GET /adoptions` nor a section's projection
 * carries a timestamp: `AdoptionPin` is `{assetId, version, assetType}` and
 * `AlertMatrixRef` / `PathwayRef` / `FieldOrigin` carry an id and a version and
 * nothing else. `AssetVersionSummary.{createdAt, createdBy, note}` is the only
 * authoritative source for when an adopted item last changed, so a surface that
 * shows "last relevant change" fetches it or invents it.
 *
 * It shares `queryKeys.assetVersions` — and therefore the cache entry — with
 * `useAssetVersionsQuery`, deliberately: opening the asset afterwards reads what
 * this already fetched. Keyed under the library root, so no preview-target
 * change invalidates it and a publish in the shared library does.
 *
 * The cost is one request per adopted asset, bounded by how many items supply
 * the section being viewed. That is the honest price of the field; the
 * alternative is a browser-side guess about when content moved.
 */
export function useAssetVersionsQueries(
  assetIds: readonly string[],
): {
  /**
   * Version rows per asset id, for the assets that answered and may still be
   * shown.
   *
   * A TERMINALLY REFUSED HISTORY IS ABSENT FROM THIS MAP, cache and all.
   * TanStack Query keeps `result.data` across a failed refetch, so a history
   * that answered once and is then refused with a 401, 403, 404, 501 or
   * terminal 409 keeps serving its last success to anything reading `.data` —
   * and on #443's block that data is a version's `createdAt`, `createdBy` and
   * release note. `api/queryClient.ts` states the rule the rest of the shell
   * follows: cached data carries a TRANSIENT outage, and a terminal status
   * fails closed. Rendering the cached rows after the service has said the
   * caller may no longer see that history is the same defect the three reads
   * above this one were already fixed for.
   */
  readonly byAssetId: ReadonlyMap<string, readonly AssetVersionSummary[]>;
  /** At least one history is still being read. */
  readonly isPending: boolean;
  /**
   * The assets whose history could not be read, BY ASSET.
   *
   * A set rather than the single `isPartial` boolean this returned until #443's
   * first review round: with three adopted items and one failed history, a
   * global flag makes all three rows say their publication date is missing, so
   * two rows that HAVE their date deny it. Consumers key on the id they render.
   */
  readonly failedAssetIds: ReadonlySet<string>;
  /**
   * The subset of those whose failure a retry could actually clear.
   *
   * A 401, 403, 404, 501 or terminal 409 is excluded, for the reason
   * `QueryErrorState` excludes it from its own retry: a control that cannot
   * work is worse than no control (#4). Callers offer recovery for exactly this
   * list, and refetch exactly these keys.
   */
  readonly retryableAssetIds: readonly string[];
} {
  const client = useApiClient();
  const results = useQueries({
    queries: assetIds.map((assetId) => ({
      queryKey: queryKeys.assetVersions(assetId),
      async queryFn(): Promise<AssetVersionList> {
        return unwrap(
          await client.GET("/v1/library/assets/{assetId}/versions", {
            params: { path: { assetId } },
          }),
        );
      },
      enabled: assetId.length > 0,
    })),
  });

  const byAssetId = new Map<string, readonly AssetVersionSummary[]>();
  const failedAssetIds = new Set<string>();
  const retryableAssetIds: string[] = [];
  results.forEach((result, index) => {
    const assetId = assetIds[index];
    if (assetId === undefined) {
      return;
    }
    // Classified BEFORE the data is published, not after. A refused refetch
    // leaves the previous success in `result.data`, and inserting it here first
    // is what let a 401/403 history keep rendering its dates and note under a
    // row the caller may no longer read.
    const refused = result.isError && isTerminalQueryError(result.error);
    if (!refused && result.data !== undefined) {
      byAssetId.set(assetId, result.data.items);
    }
    // A PAUSED history is a failure that has not settled, not a read in flight
    // (#215, refined by #327), and its recovery is the same click as any other
    // transient one. Counting it as pending is what leaves a skeleton sitting
    // in the row indefinitely with no control beside it.
    if (result.isPaused) {
      failedAssetIds.add(assetId);
      retryableAssetIds.push(assetId);
      return;
    }
    if (result.isError) {
      failedAssetIds.add(assetId);
      if (!refused) {
        retryableAssetIds.push(assetId);
      }
    }
  });

  return {
    byAssetId,
    isPending: results.some((result) => result.isPending && !result.isPaused),
    failedAssetIds,
    retryableAssetIds,
  };
}

/**
 * The row for one exact version, looked up by EQUALITY on the label.
 *
 * Never by position and never by parsing the number out of `v<N>`: the contract
 * says of `AssetVersionSummary.version` "Compare for equality; never derive the
 * next one client-side", and a list's order is not part of it. The caller
 * supplies the version it wants named — the one this project adopted, or the
 * head the catalog reports — so the answer is always a value the service chose.
 */
export function versionSummaryFor(
  versions: readonly AssetVersionSummary[] | undefined,
  version: string | null,
): AssetVersionSummary | undefined {
  if (versions === undefined || version === null) {
    return undefined;
  }
  return versions.find((summary) => summary.version === version);
}

/**
 * One exact version of one asset — what a version-detail read is keyed by.
 *
 * `AdoptionPin` satisfies it structurally and is what the callers in
 * `ui/adoptions` actually hold, but the name says what the value MEANS here: a
 * version to read, which is not necessarily a version anybody pins.
 */
export type AssetVersionRef = {
  readonly assetId: string;
  readonly version: string;
};

/**
 * The stored CONTENT of several exact versions at once (#369).
 *
 * The plural of `useAssetVersionQuery`, for a surface whose subject is a set of
 * adoptions rather than one asset — the same relationship
 * `useAssetVersionsQueries` has to `useAssetVersionsQuery`, and the same
 * classification discipline, because the hazards are identical.
 *
 * WHY IT READS THE PINNED VERSION AND NOT THE ASSET. `GET /library/assets`
 * carries no content by contract and names only `latestVersion`, so every fact
 * about what a project's pinned instrument actually administers — its items,
 * its scoring, its bands, its validity screen — exists solely at
 * `GET /library/assets/{assetId}/versions/{version}`. Reading the asset instead
 * would answer a question nobody asked: what the LIBRARY holds today, on a
 * surface whose subject is what THIS PROJECT runs.
 *
 * It shares `queryKeys.assetVersion` — and therefore the cache entry — with
 * `useAssetVersionQuery`, deliberately: opening the library asset at that
 * version afterwards reads what this already fetched. The key names the exact
 * version, so a publish in the shared library cannot move what it holds.
 *
 * A TERMINALLY REFUSED DETAIL IS ABSENT FROM THE MAP, cache and all, for the
 * reason `useAssetVersionsQueries` states: TanStack Query keeps `result.data`
 * across a failed refetch, so a version that answered once and is then refused
 * with a 401, 403, 404, 501 or terminal 409 would keep serving its last success
 * to anything reading `.data` — clinical content the service has just said the
 * caller may no longer see.
 */
export function useAssetVersionQueries(refs: readonly AssetVersionRef[]): {
  /** The version detail per asset id, for the reads that may still be shown. */
  readonly byAssetId: ReadonlyMap<string, LibraryAssetVersionDetail>;
  /** The assets whose detail is still being read, BY ASSET. */
  readonly pendingAssetIds: ReadonlySet<string>;
  /** The assets whose detail could not be read, BY ASSET. */
  readonly failedAssetIds: ReadonlySet<string>;
  /**
   * The subset of those whose retry is being HELD rather than settled.
   *
   * Reported separately because `isPaused` is a fact no consumer can recover
   * afterwards, and it is the fact that decides which vocabulary a surface may
   * use. `QueryPendingState` draws the same line for a single query: a pause
   * with no response ever received is the CONNECTION, and only that case may
   * borrow the offline wording; a pause over a failure the Editor Service
   * already answered is that failure, still holding (#215, refined by #327).
   * Flattening the two into "failed" is what left a paused read
   * indistinguishable from a settled one.
   *
   * A paused asset is always in `failedAssetIds` too: it is a failure that has
   * not settled, never a read in flight, so counting it as pending is what
   * leaves a skeleton in the row indefinitely with no control beside it.
   */
  readonly pausedAssetIds: ReadonlySet<string>;
  /**
   * The failure itself per failed asset — `error` once settled, and
   * `failureReason` while the retry is held.
   *
   * The failure rather than a code extracted from it, because the code is not
   * the only fact a surface needs: 401, 403 and 501 each have their own
   * established recovery in this shell (`QueryErrorState`), and a bare
   * `"forbidden"` string cannot reach any of them. Consumers classify with the
   * same predicates every other read uses — `ApiError`'s own getters,
   * `isTerminalQueryError`, `isServerAnsweredFailure` — so this hook and the
   * screens cannot drift into disagreeing about what a status means.
   *
   * Typed `unknown` deliberately: a transport that never connected rejects with
   * whatever `fetch` threw, and narrowing that to `Error` here would be this
   * module claiming a shape it did not receive.
   */
  readonly failureByAssetId: ReadonlyMap<string, unknown>;
  /**
   * Re-run the reads that FAILED, leaving the ones that succeeded alone.
   *
   * A batch read with no recovery is what turns one asset's 503 into a
   * permanent hole on a surface: the row that could not be read renders its
   * failure honestly and then offers the clinician nothing to do about it. The
   * successes are deliberately not refetched — they are already correct, and
   * re-reading them would spend requests to fix somebody else's row.
   *
   * Scoped to failures rather than exposing every query's `refetch`, because a
   * consumer holding per-asset refetch handles would have to decide which to
   * call, which is the classification this hook exists to own.
   *
   * NOT `result.refetch()`, and that distinction is the whole of the control.
   * `failedAssetIds` deliberately includes the PAUSED reads — a held retry is a
   * failure that has not settled — and a paused query answers a bare `refetch()`
   * by handing back the parked retryer's existing promise without firing
   * anything (`useRetryPausedQuery`, #215). The one state this button most needs
   * to clear is therefore the one a `refetch` cannot, so it runs the same
   * cancel-then-refetch every other paused recovery in this shell runs: one
   * bounded attempt per click, per key, never a prefix.
   */
  readonly retryFailed: () => void;
} {
  const client = useApiClient();
  const retryPausedByKey = useRetryPausedQueryByKey();
  const results = useQueries({
    queries: refs.map(({ assetId, version }) => ({
      queryKey: queryKeys.assetVersion(assetId, version),
      async queryFn(): Promise<LibraryAssetVersionDetail> {
        return unwrap(
          await client.GET("/v1/library/assets/{assetId}/versions/{version}", {
            params: { path: { assetId, version } },
          }),
        );
      },
      enabled: assetId.length > 0 && version.length > 0,
    })),
  });

  const byAssetId = new Map<string, LibraryAssetVersionDetail>();
  const pendingAssetIds = new Set<string>();
  const failedAssetIds = new Set<string>();
  const pausedAssetIds = new Set<string>();
  const failureByAssetId = new Map<string, unknown>();

  results.forEach((result, index) => {
    const ref = refs[index];
    if (ref === undefined) {
      return;
    }
    // Classified BEFORE the data is published, never after: a refused refetch
    // leaves the previous success in `result.data`, and inserting it here first
    // is exactly what would keep a withdrawn instrument on screen.
    const refused = result.isError && isTerminalQueryError(result.error);
    if (!refused && result.data !== undefined) {
      byAssetId.set(ref.assetId, result.data);
    }
    // A PAUSED read is a failure that has not settled, not a read in flight
    // (#215, refined by #327). Counting it as pending is what leaves a skeleton
    // in the row indefinitely, with no control beside it, over a failure the
    // Editor Service had already answered.
    if (result.isPaused) {
      failedAssetIds.add(ref.assetId);
      pausedAssetIds.add(ref.assetId);
      // `failureReason`, never `error`: a paused query has not settled, so
      // `error` is still null and the failure the held retry would be retrying
      // is here. Reading `error` is what made every pause look alike.
      failureByAssetId.set(ref.assetId, result.failureReason);
      return;
    }
    if (result.isError) {
      failedAssetIds.add(ref.assetId);
      failureByAssetId.set(ref.assetId, result.error);
      return;
    }
    if (result.isPending) {
      pendingAssetIds.add(ref.assetId);
    }
  });

  return {
    byAssetId,
    pendingAssetIds,
    failedAssetIds,
    pausedAssetIds,
    failureByAssetId,
    retryFailed() {
      for (const ref of refs) {
        if (!failedAssetIds.has(ref.assetId)) {
          continue;
        }
        retryPausedByKey(queryKeys.assetVersion(ref.assetId, ref.version));
      }
    },
  };
}

export function useAssetVersionQuery(
  assetId: string,
  version: string | null,
): UseQueryResult<LibraryAssetVersionDetail> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.assetVersion(assetId, version ?? ""),
    async queryFn() {
      return unwrap(
        await client.GET(
          "/v1/library/assets/{assetId}/versions/{version}",
          { params: { path: { assetId, version: version as string } } },
        ),
      );
    },
    enabled: assetId.length > 0 && version !== null,
  });
}

/**
 * One Rules version's substitution tables, composed against base canon (#910).
 *
 * A SEPARATE READ FROM THE VERSION, deliberately. The version read returns the
 * stored bytes and must keep doing exactly that — `#16`'s export reproduces a
 * stored identity from it — while this returns a PROJECTION: the same document
 * merged onto base canon's, with every row saying whether the runtime's value is
 * authored here or inherited. Folding the second into the first would put a
 * composed view inside the response whose hash a client is told to trust.
 *
 * `enabled` on the version, so a surface that has not resolved a head yet does
 * not fetch a projection of `""`. The only caller is mounted after the
 * `language_rules` route dispatch; the service independently refuses every
 * other family rather than returning an empty answer for the wrong asset type.
 */
export function useAssetSubstitutionsQuery(
  assetId: string,
  version: string | null,
): UseQueryResult<RulesSubstitutionProjection> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.assetVersionSubstitutions(assetId, version ?? ""),
    async queryFn() {
      return unwrap(
        await client.GET(
          "/v1/library/assets/{assetId}/versions/{version}/substitutions",
          { params: { path: { assetId, version: version as string } } },
        ),
      );
    },
    enabled: assetId.length > 0 && version !== null,
  });
}

/**
 * Which projects pin this asset, and at which version.
 *
 * Projects are shared across every authorized user of this environment
 * (amended #32/#202, 2026-08-03), so `items` is the adoption set — consumers
 * count the rows. #202 B1 (PR #226) removed the contract's separate
 * `totalAdoptionCount`, so a count that disagrees with the rows is no longer
 * expressible.
 */
export function useAssetAdoptionsQuery(
  assetId: string,
  {
    enabled = true,
    decisionFresh = false,
  }: { enabled?: boolean; decisionFresh?: boolean } = {},
): UseQueryResult<AssetAdoptionList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.assetAdoptions(assetId),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/library/assets/{assetId}/adoptions", {
          params: { path: { assetId } },
          signal,
        }),
      );
    },
    enabled: enabled && assetId.length > 0,
    // `decisionFresh` is for the surface where these rows justify a lifecycle
    // WRITE (the archive/unarchive dialog): a decision must be made on rows
    // read now, not on rows up to `staleTime` old — the audit's PW-LIB-10
    // "stale adoption accounting" (#648) was a count read before the pin
    // change it no longer described. Passive renders keep the shared default.
    ...(decisionFresh
      ? { staleTime: 0, refetchOnMount: "always" as const }
      : {}),
  });
}

/**
 * One project's whole pin set (#35's pinning surface).
 *
 * The complementary read to `useAssetAdoptionsQuery`: that one answers "which
 * projects pin this asset", this one answers "what does this project pin", and
 * the replacement write needs the second because the endpoint replaces the
 * WHOLE set. A pin surface that sent only its own asset would silently unpin
 * every other adoption the project holds.
 *
 * Keyed under the project root rather than the library one: a pin belongs to
 * the project, and an asset-side invalidation must not silently drop it.
 */
export function useProjectAdoptionsQuery(
  projectId: string,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<ProjectAdoptions> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projectAdoptions(projectId),
    async queryFn() {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/adoptions", {
          params: { path: { projectId } },
        }),
      );
    },
    enabled: enabled && projectId.length > 0,
  });
}

/**
 * `POST …/preflight` for one lane: what writing this exact pin set WOULD do,
 * decided by the same evaluator the write runs, and writing nothing.
 *
 * WHY THE DIALOG ASKS THIS BEFORE THE FIRST CLICK. Some of what a pin
 * displaces is not derivable from the catalog: base canon's Anxiety stores no
 * coordinate, so the browser cannot know that an authored copy placed at the
 * same runtime placement REPLACES it rather than joining it. The server can —
 * `placement.ownership_conflict` names the incumbent by id in
 * `ownershipConflicts` — and a confirmation that stated one write and then
 * had the service refuse it for a replacement it could not see is the dead
 * end #632 closed for every other refusal. So the decision is read first, and
 * the sentence a clinician confirms is the one the server decided.
 *
 * `items === null` means "do not ask" — a removal never needs it, and a
 * decision whose inputs have not loaded has nothing to ask about. `retry:
 * false` for the mutations' reason: every answer here is decided by state a
 * retry does not change, and a refusal is an answer, not a fault.
 *
 * NOT a gate on the write. A dry run that could not be read (a broken mount, a
 * project the caller cannot see) leaves the write to decide, as it always
 * did; the write refuses with the same typed shape and the dialog reads the
 * replacement plan from that refusal instead. One reader for both.
 */
export function useAdoptionPreflightQuery(
  projectId: string,
  lane: AdoptionWriteLane,
  items: readonly AdoptionPinRequest[] | null,
): UseQueryResult<AdoptionPreflight> {
  const client = useApiClient();
  const requested = items ?? [];
  return useQuery({
    queryKey: queryKeys.adoptionPreflight(projectId, lane, requested),
    async queryFn(): Promise<AdoptionPreflight> {
      const params = { path: { projectId } };
      const body = { items: [...requested] };
      if (lane === "pathway-activation") {
        return unwrap(
          await client.POST("/v1/projects/{projectId}/pathway-activation/preflight", {
            params,
            body,
          }),
        );
      }
      if (lane === "main-concern-activation") {
        return unwrap(
          await client.POST("/v1/projects/{projectId}/main-concern-activation/preflight", {
            params,
            body,
          }),
        );
      }
      if (lane === "closing-activation") {
        return unwrap(
          await client.POST("/v1/projects/{projectId}/closing-activation/preflight", {
            params,
            body,
          }),
        );
      }
      if (lane === "background-activation") {
        return unwrap(
          await client.POST("/v1/projects/{projectId}/background-activation/preflight", {
            params,
            body,
          }),
        );
      }
      return unwrap(
        await client.POST("/v1/projects/{projectId}/adoptions/preflight", {
          params,
          body,
        }),
      );
    },
    enabled: items !== null && projectId.length > 0,
    retry: false,
    staleTime: 0,
  });
}

/**
 * Replace the project's pin set under optimistic concurrency.
 *
 * `expectedAdoptionsVersion` is the identity the caller LOADED and showed the
 * clinician, never one re-read at submit time — the same discipline
 * `expectedHeadVersion` carries on a version publish, and for the same reason:
 * re-reading it here would turn the check into a formality that always passes
 * and always clobbers somebody else's pin.
 */
export function useReplaceProjectAdoptionsMutation(
  projectId: string,
): UseMutationResult<
  ProjectAdoptions,
  unknown,
  ReplaceProjectAdoptionsRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);

  return useMutation({
    async mutationFn(body: ReplaceProjectAdoptionsRequest) {
      return unwrap(
        await client.PUT("/v1/projects/{projectId}/adoptions", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess() {
      await settleAdoptionWrite(queryClient, projectId, clearValidation);
    },
    onError(error) {
      settleStaleAdoptions(queryClient, projectId, clearValidation, error);
    },
    // Whole-set replacement is not idempotent against a moving version, and
    // every documented refusal here — stale, archived, over the matrix cap — is
    // decided by state a retry does not change.
    retry: false,
  });
}

/**
 * What every mounted reader has to re-observe after a pin set moved — shared
 * by the whole-set replacement above and the Pathway activation below, so the
 * two writers cannot leave a different set of readers stale.
 *
 * A passed report certifies the exact base + adoption pins + optional draft
 * tuple that was loaded when validation ran. Adoption-only workspaces retain
 * the sentinel `dv-initial`, so comparing only draft versions cannot expose
 * this move: without clearing the report, the candidate gate would remain
 * enabled for content the report never saw.
 *
 * `GET /draft` carries the durable validation badge as well as the workspace —
 * and after a Pathway activation it carries the NEW draft version that holds
 * the registration. Refetch it so every mounted reader observes the server's
 * invalidated verdict, not the badge cached before this write. The mutation
 * stays pending until every mounted reader has consumed the write: the row
 * dialog closes when `mutateAsync` resolves, and fire-and-forget invalidation
 * let it close over the previous section summary for a visible interval (and,
 * on a slow projection, until a reload). Awaiting the refetch makes the
 * compact list itself the confirmation.
 */
async function settleAdoptionWrite(
  queryClient: QueryClient,
  projectId: string,
  clearValidation: (projectId: string) => void,
): Promise<void> {
  clearValidation(projectId);
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.draft(projectId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectOverview(projectId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectAdoptions(projectId),
    }),
    // The asset-side impact view is the same fact read from the other end:
    // this project just appeared in, left, or moved within it.
    queryClient.invalidateQueries({ queryKey: ["library"] }),
    // And the EFFECTIVE configuration, which is the pin set resolved into
    // fields. NEITHER key above reaches it: the projection is keyed
    // `["projects", id, "sections", <section>]`, which is not under
    // `…, "adoptions"` and not under `["library"]`. Without this, the lock
    // and provenance every row renders keep describing the pin set as it was
    // BEFORE the write — a freshly pinned floor shows no `Supplies …` line,
    // and an unreadable-projection notice outlives the read that failed —
    // until a reload or `Read it again`.
    //
    // The whole prefix rather than one section, deliberately: an adoption
    // supplies fields into whichever sections its asset covers, and the
    // request body does not say which those are. Invalidating all seven
    // costs a refetch of queries that are already shared with
    // `useSectionQuery`; getting it wrong costs a clinician a stale lock.
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectSections(projectId),
    }),
    // And the frozen candidates' server-derived FRESHNESS: an adoption
    // added, removed or replaced supersedes the newest candidate (its
    // pin-set digest no longer matches the Project's), and the Preview modal
    // launches only on that reading. Left cached, a modal opened within the
    // listing's lifetime would still call the version "Current".
    queryClient.invalidateQueries({
      queryKey: queryKeys.previewCandidates(projectId),
    }),
    // AND THE TYPED FLOW STATE (#855). An adoption changes which Pathway pins
    // exist and, through the server's own re-derivation, the Always/automatic
    // selection persisted with each of them — so `GET /flow`'s
    // `adoptedPathways` answers differently the moment this write lands. The
    // key is `["projects", id, "flow"]`, which is under NONE of the prefixes
    // above: without this, the Pathways section renders the pin it just
    // adopted under "No runtime selection" — the service having reported a
    // selection for it — until a reload. Measured on the real service, not
    // theorised.
    queryClient.invalidateQueries({ queryKey: queryKeys.projectFlow(projectId) }),
  ]);
}

/**
 * A stale refusal means somebody else's write landed, so the set on screen —
 * and the impact rendered from it — describes a version that no longer exists.
 *
 * The refusal is authoritative news that another write already changed the
 * tuple. Invalidate every validation projection and revoke this tab's session
 * report just as a successful local write does; otherwise the losing tab can
 * keep displaying Passed.
 */
function settleStaleAdoptions(
  queryClient: QueryClient,
  projectId: string,
  clearValidation: (projectId: string) => void,
  error: unknown,
): void {
  if (error instanceof ApiError && error.code === "stale_adoptions_version") {
    clearValidation(projectId);
    void queryClient.invalidateQueries({
      queryKey: queryKeys.draft(projectId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.projectOverview(projectId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.projectAdoptions(projectId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.previewCandidates(projectId),
    });
    // The same reason the successful path drops it (#855): the losing tab's
    // Always/Dynamic groups were derived from a pin set the service has
    // already replaced.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.projectFlow(projectId),
    });
  }
}

export type PathwayActivation = components["schemas"]["PathwayActivation"];
export type PathwayActivationRequest =
  components["schemas"]["PathwayActivationRequest"];
export type PathwayActivationPreflight =
  components["schemas"]["PathwayActivationPreflight"];

/**
 * `POST /v1/projects/{projectId}/pathway-activation` — adopt a Pathway pin set
 * AND register its runtime coordinates, in one server-owned transaction (#749).
 *
 * WHY A SECOND WRITER. `PUT /adoptions` replaces the whole pin set and may not
 * touch the draft, so an authored Pathway family or variant — whose coordinate
 * the project's runtime registration does not yet name — is refused by #632's
 * preflight before anything is written, and nothing in the product could write
 * that registration (the #644 finding). This operation is the governed
 * control: the browser sends the COMPLETE Pathway pin set under the whole-set
 * token it loaded, and the Editor Service derives the `dynamic_domains`
 * registration those exact pins need from their own server-owned coordinates,
 * decides every pin against that PROPOSED registration, and persists pins and
 * registration together — or neither. No coordinate, path or list ever leaves
 * this browser (ratified §2.1), and pins of every other family are preserved
 * server-side rather than re-sent.
 *
 * The refusal shape is `PUT /adoptions`' own (`adoption_preflight_refused`,
 * `stale_adoptions_version`), so the dialog renders one recovery for both.
 */
export function useActivateProjectPathwaysMutation(
  projectId: string,
): UseMutationResult<PathwayActivation, unknown, PathwayActivationRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);

  return useMutation({
    async mutationFn(body: PathwayActivationRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/pathway-activation", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess() {
      await settleAdoptionWrite(queryClient, projectId, clearValidation);
    },
    onError(error) {
      settleStaleAdoptions(queryClient, projectId, clearValidation, error);
    },
    // Not idempotent against a moving token, and every documented refusal —
    // stale, parent required, over the Dynamic cap, unplaced — is decided by
    // state a retry does not change.
    retry: false,
  });
}

export type MainConcernActivation =
  components["schemas"]["MainConcernActivation"];
export type MainConcernActivationRequest =
  components["schemas"]["MainConcernActivationRequest"];
export type MainConcernActivationPreflight =
  components["schemas"]["MainConcernActivationPreflight"];

/**
 * `POST /v1/projects/{projectId}/main-concern-activation` — adopt a Main
 * concern pin AND register its runtime coordinate, in one server-owned
 * transaction (#634, the Main-concern analogue of the Pathway activation).
 *
 * WHY A THIRD WRITER. The engine derives its single opening evaluation from
 * `init_track`, so an authored Main concern — whose coordinate the project's
 * registration does not name — is refused by #632's preflight before the
 * whole-set replacement writes anything, and nothing in the product could
 * write that registration (the `MC-PRJ-05` dead end #732 left open). This is
 * the governed control: the browser sends the Main concern pin the project
 * should hold — one exact version, or none — under the whole-set token it
 * loaded, and the Editor Service derives the single-eval `init_track`
 * registration from the row's own server-owned coordinate, decides the pin
 * against that PROPOSED registration, and persists pin and registration
 * together — or neither. An explicit replacement moves the single pin and
 * its registration in one write, so no two-pin or merged state can exist; a
 * removal, or base canon's own Main concern, reverts the registration so the
 * base pack supplies the section again. No coordinate, path or list ever
 * leaves this browser (ratified §2.1), and every other family's pin is
 * preserved server-side rather than re-sent.
 *
 * The refusal shape is `PUT /adoptions`' own (`adoption_preflight_refused`,
 * `stale_adoptions_version`), so the dialog renders one recovery for all
 * four writers (#867 added the closing lane beside these).
 */
export function useActivateProjectMainConcernMutation(
  projectId: string,
): UseMutationResult<MainConcernActivation, unknown, MainConcernActivationRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);

  return useMutation({
    async mutationFn(body: MainConcernActivationRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/main-concern-activation", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess() {
      await settleAdoptionWrite(queryClient, projectId, clearValidation);
    },
    onError(error) {
      settleStaleAdoptions(queryClient, projectId, clearValidation, error);
    },
    // Not idempotent against a moving token, and every documented refusal —
    // stale, a second Main concern, an unreachable or incomplete coordinate —
    // is decided by state a retry does not change.
    retry: false,
  });
}

export type ClosingConversationAsset = Extract<
  LibraryAsset,
  { assetType: "closing_conversation" }
>;
export type ClosingActivation = components["schemas"]["ClosingActivation"];
export type ClosingActivationRequest =
  components["schemas"]["ClosingActivationRequest"];
export type ClosingActivationPreflight =
  components["schemas"]["ClosingActivationPreflight"];

/**
 * `POST /v1/projects/{projectId}/closing-activation` — adopt the Closing
 * conversation pin AND wire the optional terminal closing node, in one
 * server-owned transaction (#866's route, consumed here by #867).
 *
 * WHY A FOURTH WRITER, and its reason is the sharpest of the three. `PUT
 * /adoptions` replaces the whole pin set and may not touch the draft, and this
 * family's runtime registration is a PAIR — `track_states.conclusion` and
 * `conclusion_track` — which the pinned engine refuses to load with only one
 * half present. So a Closing pin adopted through the whole-set route would be
 * refused by #632's preflight before anything was written, and nothing in the
 * product could write the registration that makes it reachable. This operation
 * is the governed control: the browser sends the Closing pin the project should
 * hold — one exact immutable version, or none — under the whole-set token it
 * loaded, and the Editor Service derives BOTH enabler keys from the family's
 * server-owned target and the mounted base pack, decides the pin against that
 * PROPOSED registration, and persists pin and registration together — or
 * neither. An empty set reverts both keys, so the project inherits the base
 * pack's own closing configuration, which for the base pack is no closing node
 * at all. No coordinate, path, token or list ever leaves this browser (ratified
 * §2.1), and every other family's pin is preserved server-side rather than
 * re-sent.
 *
 * The refusal shape is `PUT /adoptions`' own (`adoption_preflight_refused`,
 * `stale_adoptions_version`), so the dialog renders one recovery for every
 * writer.
 */
export function useActivateProjectClosingConversationMutation(
  projectId: string,
): UseMutationResult<ClosingActivation, unknown, ClosingActivationRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);

  return useMutation({
    async mutationFn(body: ClosingActivationRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/closing-activation", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess() {
      await settleAdoptionWrite(queryClient, projectId, clearValidation);
    },
    onError(error) {
      settleStaleAdoptions(queryClient, projectId, clearValidation, error);
    },
    // Not idempotent against a moving token, and every documented refusal —
    // stale, a second Closing conversation, an incomplete or unreachable pair —
    // is decided by state a retry does not change.
    retry: false,
  });
}

export type BackgroundActivation = components["schemas"]["BackgroundActivation"];
export type BackgroundActivationRequest =
  components["schemas"]["BackgroundActivationRequest"];
export type BackgroundActivationPreflight =
  components["schemas"]["BackgroundActivationPreflight"];

/**
 * `POST /v1/projects/{projectId}/background-activation` — adopt the BG pin set
 * AND select the interview variant those pins need, in one server-owned
 * transaction (#858, wired to the product surface by #914).
 *
 * WHY A FIFTH WRITER, and it is the same dead end the others closed. #857
 * gave an authored BG domain the coordinate `<variant>/<domain>`, and
 * `placement.reachability` correctly refuses it whenever the project's
 * effective `flow_config.bg_variant` names a different variant — which, for a
 * project that has never selected one, is always: the engine's baseline is
 * `yadbeyad` and nothing in the product could write anything else. So through
 * `PUT /adoptions` an authored profile and its domains can be published and
 * never adopted.
 *
 * WHAT MAKES THIS LANE UNAVOIDABLE RATHER THAN MERELY BETTER. The two families
 * are refused SEPARATELY and accepted TOGETHER: a profile with no domain at its
 * variant is `lonely_profile`, domains with no profile behind them are
 * `unbacked_domains`, and both arrive as `placement.background_profile_conflict`
 * (`background_profile.variant_incoherences`). There is therefore no order in
 * which two single-family writes could reach a coherent set, which is why the
 * BG section adopts through this one request and never through the whole-set
 * replacement.
 *
 * The browser sends the COMPLETE set of BG profile and BG domain pins under the
 * whole-set token it loaded; the Editor Service derives `rules.bg_variant` from
 * the pins' own server-owned coordinates and `rules.bg_categories` from the
 * pinned documents' declared order, decides every pin against that PROPOSED
 * registration, and persists pins and registration together — or neither. No
 * variant name, coordinate or domain list ever leaves this browser (ratified
 * §2.1), and pins of every other family are preserved server-side rather than
 * re-sent.
 *
 * The refusal shape is `PUT /adoptions`' own (`adoption_preflight_refused`,
 * `stale_adoptions_version`), so one recovery serves all five writers.
 */
export function useActivateProjectBackgroundMutation(
  projectId: string,
): UseMutationResult<BackgroundActivation, unknown, BackgroundActivationRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);

  return useMutation({
    async mutationFn(body: BackgroundActivationRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/background-activation", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess() {
      await settleAdoptionWrite(queryClient, projectId, clearValidation);
    },
    onError(error) {
      settleStaleAdoptions(queryClient, projectId, clearValidation, error);
    },
    // Not idempotent against a moving token, and every documented refusal —
    // stale, a second profile, an incoherent variant, a language the profile
    // does not carry — is decided by state a retry does not change.
    retry: false,
  });
}

/**
 * Publish the next immutable version (#33's Save).
 *
 * The whole content goes up, not a patch: this endpoint appends a version, and
 * a version is a complete document by definition. Sparse typed changes are the
 * project-draft overlay's model (`patchDraft`), a different endpoint with a
 * different payload — no endpoint produces both.
 *
 * `expectedHeadVersion` is the pin that makes AC2 true, and it is the CALLER's
 * to supply: it must be the head the clinician actually read and edited, never
 * a value re-read at submit time, or the check certifies nothing. A refusal
 * comes back as 409 `stale_asset_version` carrying `currentAssetVersion`.
 */
export function useCreateAssetVersionMutation(
  assetId: string,
): UseMutationResult<
  LibraryAssetVersionDetail,
  unknown,
  CreateAssetVersionRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(body: CreateAssetVersionRequest) {
      return unwrap(
        await client.POST("/v1/library/assets/{assetId}/versions", {
          params: { path: { assetId } },
          body,
        }),
      );
    },
    onSuccess() {
      // The head moved, so the asset read, its history, and both catalog
      // variants are all stale. The adoption view is not — pins are explicit and
      // a new version adopts nobody — but it lives under the same root and a
      // narrower invalidation here would be a rule to maintain rather than a
      // saving.
      void queryClient.invalidateQueries({ queryKey: ["library"] });
    },
    onError(error) {
      // A stale refusal is news about the asset, not just about this request:
      // somebody else's version landed, and every other panel on the page —
      // the head in the title, the version history, the catalog row — is now
      // describing a head that no longer exists. Refetching here is what makes
      // "you are shown the current version" true of the whole page rather than
      // only of the recovery banner.
      if (error instanceof ApiError && error.code === "stale_asset_version") {
        void queryClient.invalidateQueries({ queryKey: ["library"] });
      }
    },
    // A refused publish is never retried automatically. Every documented
    // refusal — seeded, archived, stale, or invalid — is decided by state a
    // retry does not change, and the one thing this write must never do is
    // land twice.
    retry: false,
  });
}

/**
 * Create a shared asset at v1, from scratch or as a copy.
 *
 * A copy is this same request carrying content read from the source's version
 * detail; there is no server-side copy route and no need for one, because the
 * copy is exactly "a new clinician-authored asset that starts where the seeded
 * one left off". That is the route the service's own 403 prescribes — "copy it
 * into a new asset or override per project" — and it is what makes seeded base
 * canon read-only without making it a dead end.
 */
export function useCreateLibraryAssetMutation(): UseMutationResult<
  LibraryAsset,
  unknown,
  CreateLibraryAssetRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(body: CreateLibraryAssetRequest) {
      return unwrap(await client.POST("/v1/library/assets", { body }));
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["library"] });
    },
    retry: false,
  });
}

/**
 * Where a lifecycle write is, in finer grain than `isPending`.
 *
 * `isPending` covers the whole of `mutateAsync` — the POST AND the awaited
 * library refresh in `onSuccess` — and the two halves mean opposite things to
 * the dialog holding the request. During `writing` a non-idempotent request is
 * in flight and dismissing the dialog would neither cancel it nor say what
 * happened, so the dialog locks. During `refreshing` the service has already
 * accepted the write; what is in flight is a set of READS, and a read can be
 * held for as long as the connection is gone (`queryClient.ts` on paused
 * retries). Locking the dialog on that half left a clinician with no exit from
 * an archive that had already succeeded (PR #675 review).
 */
export type LifecyclePhase = "idle" | "writing" | "refreshing";

export type LifecycleMutationResult = UseMutationResult<
  LibraryAsset,
  unknown,
  void
> & { phase: LifecyclePhase };

function useLifecycleMutation(
  assetId: string,
  path:
    | "/v1/library/assets/{assetId}/archive"
    | "/v1/library/assets/{assetId}/unarchive",
): LifecycleMutationResult {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<LifecyclePhase>("idle");

  const mutation = useMutation({
    onMutate() {
      setPhase("writing");
    },
    async mutationFn() {
      return unwrap(
        await client.POST(path, { params: { path: { assetId } } }),
      );
    },
    async onSuccess(head) {
      // FIRST, synchronously, the cache is reconciled from the service's own
      // answer. `head` is the asset as the server now holds it — not an
      // optimistic guess, so no lifecycle metadata is fabricated (#648 DoD) —
      // and it is written into every entry that renders this asset's archive
      // state before any read is even issued: the active-only list drops or
      // gains the row, the include-archived list carries the new capabilities,
      // and the asset's own head is replaced. This is what makes "the row is
      // gone the moment the write is acknowledged" a property of the code
      // rather than of a refetch's latency: a slow or held refresh cannot show
      // a pre-archive row, because there is no pre-archive row left to show
      // (PR #675 review, PW-LIB-09's just-archived-row window).
      //
      // Updaters return `undefined` for an entry that was never fetched, which
      // TanStack treats as "leave it alone" — nothing is conjured for a list
      // nobody has read. A row the server MOVED goes to the front: `set_archived`
      // bumps `updated_at` on a real flip, and `list_assets` serves
      // newest-first. A row the server did NOT move keeps its place: that is
      // the no-op write whose cached row already IS the server head
      // (`updatedAt` unchanged). If another writer flipped the asset first,
      // `updatedAt` differs and the row is front-inserted — which is where
      // the service now lists it.
      const withHead = (items: LibraryAsset[]): LibraryAsset[] => {
        const current = items.find((item) => item.id === head.id);
        if (current !== undefined && current.updatedAt === head.updatedAt) {
          return items.map((item) => (item.id === head.id ? head : item));
        }
        return [head, ...items.filter((item) => item.id !== head.id)];
      };
      // Each entry keeps the age of the fetch it came from. `setQueryData`
      // would otherwise stamp the reconcile instant as `dataUpdatedAt`, and
      // `StaleDataNotice` — visible in exactly the held-refresh state this
      // reconcile exists for — would then date rows as older than they are
      // by the age of a refresh that never landed.
      const keepAge = (queryKey: readonly unknown[]) => {
        const updatedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt;
        return updatedAt === undefined ? {} : { updatedAt };
      };
      const activeKey = queryKeys.libraryAssets(false);
      queryClient.setQueryData<LibraryAssetList>(
        activeKey,
        (list) =>
          list === undefined
            ? undefined
            : {
                ...list,
                items: head.capabilities.archived
                  ? list.items.filter((item) => item.id !== head.id)
                  : withHead(list.items),
              },
        keepAge(activeKey),
      );
      const archivedKey = queryKeys.libraryAssets(true);
      queryClient.setQueryData<LibraryAssetList>(
        archivedKey,
        (list) =>
          list === undefined ? undefined : { ...list, items: withHead(list.items) },
        keepAge(archivedKey),
      );
      const headKey = queryKeys.libraryAsset(head.id);
      queryClient.setQueryData<LibraryAsset>(
        headKey,
        (current) => (current === undefined ? undefined : head),
        keepAge(headKey),
      );

      // THEN the phase flips: the write is committed and the cache already
      // tells the truth, so the dialog may hand control back while the reads
      // below settle.
      setPhase("refreshing");

      // Everything under the library root: the catalog lists (both
      // includeArchived variants), this asset, and its adoption view all
      // describe the asset's head state, which just changed — and the
      // reconcile above covers only what the response carries. Awaited, not
      // fire-and-forget, the same discipline as the pin replacement above: the
      // dialog's normal close waits for service truth to land.
      await queryClient.invalidateQueries({ queryKey: ["library"] });
    },
    onSettled() {
      setPhase("idle");
    },
    // Archive and unarchive are idempotent by contract, but a failed attempt
    // still deserves the clinician's eyes before a second one: the 403 and 409
    // answers here mean "this asset refuses the write", which no retry fixes.
    retry: false,
  });

  return { ...mutation, phase };
}

/** Archive: the only lifecycle exit. Existing adoptions keep resolving. */
export function useArchiveAssetMutation(
  assetId: string,
): LifecycleMutationResult {
  return useLifecycleMutation(
    assetId,
    "/v1/library/assets/{assetId}/archive",
  );
}

export function useUnarchiveAssetMutation(
  assetId: string,
): LifecycleMutationResult {
  return useLifecycleMutation(
    assetId,
    "/v1/library/assets/{assetId}/unarchive",
  );
}
